import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    model,
    output,
    signal,
    untracked,
} from '@angular/core';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmationDialogService} from '@brunata-angular/ui';
import {TranslatePipe, TranslateService} from '@brunata-angular/util';
import {SavedExport, SavedExportCreateRequest, savedExportUsage} from '../saved-export.model';
import {ActionButtonComponent} from '../../shared/ui/action-button/action-button.component';
import {ExportTestButtonComponent} from '../../shared/ui/export-test-button/export-test-button.component';
import {StepFlowComponent} from '../../steps/step-flow/step-flow.component';
import {
    CustomizedExportsFormService,
    customizedExportFormProviders,
} from '../../customized-exports/customized-exports-form.service';
import {
    CustomizedFlowContext,
    slotIdsForUsage,
    slotsForIds,
} from '../../customized-exports/customized-exports.model';

@Component({
    selector: 'app-edit-customized-export-modal',
    standalone: true,
    imports: [TranslatePipe, StepFlowComponent, ExportTestButtonComponent, ActionButtonComponent],
    templateUrl: './edit-customized-export-modal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: customizedExportFormProviders,
})
export class EditCustomizedExportModalComponent {
    readonly savedExport = model.required<SavedExport>();
    readonly save = output<SavedExportCreateRequest>();
    readonly isSaving = signal(false);

    private readonly activeModal = inject(NgbActiveModal);
    protected readonly formService = inject(CustomizedExportsFormService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly translateService = inject(TranslateService);

    private readonly context: CustomizedFlowContext = {form: this.formService};

    protected readonly slots = computed(() =>
        slotsForIds(
            slotIdsForUsage(savedExportUsage(this.savedExport())).filter(id => id !== 'usage'),
        ).map(slot => ({
            id: slot.id,
            titleKey: slot.titleKey,
            body: {render: slot.render, bindings: slot.bind(this.context)},
        })),
    );

    constructor() {
        effect(() => {
            const savedExport = this.savedExport();
            untracked(() => this.formService.loadFromSavedExport(savedExport));
        });
    }

    protected onSave(): void {
        if (!this.formService.validateForSave()) {
            return;
        }
        this.save.emit(this.formService.buildSaveRequest());
    }

    protected cancel(): void {
        if (!this.formService.hasUnsavedChanges()) {
            this.activeModal.dismiss();
            return;
        }
        void this.confirmationDialogService
            .confirmWithSettings({
                title: this.translateService.translate('common_warning', 'online', 'common'),
                message: this.translateService.translate(
                    'savedexport_edit_cancel_warning',
                    'online',
                    'export',
                ),
                centered: true,
            })
            .then(confirmed => {
                if (confirmed) {
                    this.activeModal.dismiss();
                }
            });
    }
}
